// 📄 pages/SAP-BTP/components/FormDatosIflow.tsx
// Datos que escribe el usuario de un iFlow (subir y editar). El proyecto se
// elige solo entre los proyectos del usuario que pertenecen a la org del
// repositorio (el backend vuelve a validarlo).
import React, { useMemo } from 'react';
import PillMultiSelect from '../../../components/ui/PillMultiSelect.tsx';
import { useUserProjects } from '../../../context/ProjectsContext.tsx';
import { DatosIflow, ESTADOS_IFLOW, EstadoIflow } from '../../../services/iflowService.ts';

export const inputCls = 'w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50';
export const labelCls = 'block text-xs font-medium text-gray-400 mb-1';

interface FormDatosIflowProps {
  orgId: string;
  datos: DatosIflow;
  onChange: (datos: DatosIflow) => void;
}

export default function FormDatosIflow({ orgId, datos, onChange }: FormDatosIflowProps) {
  const { proyectos } = useUserProjects();
  const proyectosDeOrg = useMemo(() => proyectos.filter((p) => p.orgId === orgId), [proyectos, orgId]);
  const cambiar = (campo: keyof DatosIflow, valor: any) => onChange({ ...datos, [campo]: valor });
  // Un proyecto ya guardado al que el usuario no pertenece (ej. lo eligió
  // otro miembro) se sigue mostrando para no perderlo al editar.
  const proyectoAjeno = datos.proyectoId && !proyectosDeOrg.some((p) => p.id === datos.proyectoId);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="md:col-span-2">
        <label className={labelCls}>Título *</label>
        <input className={inputCls} maxLength={120} value={datos.titulo} onChange={(e) => cambiar('titulo', e.target.value)} />
      </div>
      <div className="md:col-span-2">
        <label className={labelCls}>Descripción *</label>
        <textarea className={`${inputCls} min-h-[90px]`} maxLength={2000} value={datos.descripcion} onChange={(e) => cambiar('descripcion', e.target.value)} placeholder="Qué hace el flujo, cuándo se usa…" />
      </div>
      <div>
        <label className={labelCls}>Sistema origen</label>
        <input className={inputCls} maxLength={80} value={datos.sistemaOrigen || ''} onChange={(e) => cambiar('sistemaOrigen', e.target.value)} placeholder="Ej. S/4HANA" />
      </div>
      <div>
        <label className={labelCls}>Sistema destino</label>
        <input className={inputCls} maxLength={80} value={datos.sistemaDestino || ''} onChange={(e) => cambiar('sistemaDestino', e.target.value)} placeholder="Ej. SAP Ariba" />
      </div>
      <div>
        <label className={labelCls}>Solución / proceso</label>
        <input className={inputCls} maxLength={80} value={datos.solucion || ''} onChange={(e) => cambiar('solucion', e.target.value)} placeholder="Ej. Commerce, SLP, Facturación" />
      </div>
      <div>
        <label className={labelCls}>Estado</label>
        <select className={inputCls} value={datos.estado || 'borrador'} onChange={(e) => cambiar('estado', e.target.value as EstadoIflow)}>
          {ESTADOS_IFLOW.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
        </select>
      </div>
      <div className="md:col-span-2">
        <label className={labelCls}>Proyecto de origen (opcional)</label>
        <select className={inputCls} value={datos.proyectoId || ''} onChange={(e) => cambiar('proyectoId', e.target.value)}>
          <option value="">Sin proyecto</option>
          {proyectoAjeno && <option value={datos.proyectoId}>(proyecto actual)</option>}
          {proyectosDeOrg.map((p) => <option key={p.id} value={p.id}>{p.nombre}{p.clienteNombre ? ` · ${p.clienteNombre}` : ''}</option>)}
        </select>
      </div>
      <div className="md:col-span-2">
        <PillMultiSelect label="Etiquetas" valores={datos.etiquetas || []} onChange={(v) => cambiar('etiquetas', v.slice(0, 10))} placeholder="Escribe y presiona Enter" />
      </div>
      <div className="md:col-span-2">
        <label className={labelCls}>Notas de configuración</label>
        <textarea className={`${inputCls} min-h-[70px]`} maxLength={4000} value={datos.notas || ''} onChange={(e) => cambiar('notas', e.target.value)} placeholder="Credenciales, certificados o parámetros a configurar al importarlo…" />
      </div>
    </div>
  );
}
